import { z } from 'zod';

/**
 * Environment variable validation schema
 * Validates all required and optional environment variables at app startup
 */
const envSchema = z.object({
  REACT_APP_API_BASE: z.string().url('REACT_APP_API_BASE must be a valid URL'),
  REACT_APP_API_EXPORT_PDF: z.string().url('REACT_APP_API_EXPORT_PDF must be a valid URL').optional(),
  REACT_APP_APIM_KEY: z.string().min(1, 'REACT_APP_APIM_KEY cannot be empty').optional(),
  REACT_APP_STORAGE_ENCRYPTION_KEY: z.string().min(32, 'REACT_APP_STORAGE_ENCRYPTION_KEY must be at least 32 characters').optional(),
  REACT_APP_API_GOOGLE_MAP: z.string().url('REACT_APP_API_GOOGLE_MAP must be a valid URL').optional(),
  REACT_APP_RISK_COACH_API_KEY: z.string().min(1, 'REACT_APP_RISK_COACH_API_KEY cannot be empty').optional(),
  REACT_APP_API_RISK_COACH: z.string().url('REACT_APP_API_RISK_COACH must be a valid URL').optional(),
  REACT_APP_HOTJAR_ID: z.string().optional(),
  REACT_APP_HOTJAR_SNIPPET_VERSION: z.string().optional(),
  REACT_APP_GA_IDENTIFIER: z.string().optional(),
  REACT_APP_PAYWAY_API_KEY: z.string().optional(),
  NODE_ENV: z.enum(['development', 'production', 'test'], {
    errorMap: () => ({ message: 'NODE_ENV must be development, production, or test' })
  }),
});

/**
 * Validates environment variables at runtime
 * @returns {boolean} True if validation passes, false otherwise
 * @throws {Error} In production if validation fails
 */
// eslint-disable-next-line import/prefer-default-export
export const validateEnv = () => {
  try {
    const env = {
      REACT_APP_API_BASE: process.env.REACT_APP_API_BASE,
      REACT_APP_API_EXPORT_PDF: process.env.REACT_APP_API_EXPORT_PDF,
      REACT_APP_APIM_KEY: process.env.REACT_APP_APIM_KEY,
      REACT_APP_STORAGE_ENCRYPTION_KEY: process.env.REACT_APP_STORAGE_ENCRYPTION_KEY,
      REACT_APP_API_GOOGLE_MAP: process.env.REACT_APP_API_GOOGLE_MAP,
      REACT_APP_RISK_COACH_API_KEY: process.env.REACT_APP_RISK_COACH_API_KEY,
      REACT_APP_API_RISK_COACH: process.env.REACT_APP_API_RISK_COACH,
      REACT_APP_HOTJAR_ID: process.env.REACT_APP_HOTJAR_ID,
      REACT_APP_HOTJAR_SNIPPET_VERSION: process.env.REACT_APP_HOTJAR_SNIPPET_VERSION,
      REACT_APP_GA_IDENTIFIER: process.env.REACT_APP_GA_IDENTIFIER,
      REACT_APP_PAYWAY_API_KEY: process.env.REACT_APP_PAYWAY_API_KEY,
      NODE_ENV: process.env.NODE_ENV,
    };
    
    envSchema.parse(env);
    return true;
  } catch (error) {
    const errorMessage = error.errors?.map(e => `${e.path.join('.')}: ${e.message}`).join('\n') || error.message;
    console.error('Environment validation failed:', errorMessage);
    
    if (process.env.NODE_ENV === 'production') {
      // In production, throw error to prevent app from starting with invalid config
      throw new Error(`Invalid environment configuration:\n${errorMessage}`);
    } else {
      // In development, log warning but allow app to continue
      console.warn('⚠️  Environment validation warnings (app will continue in development mode):', errorMessage);
      return false;
    }
  }
};
